/**
 * The AI model registry: which operations exist, where each one goes, and what each may name.
 *
 * Like `shared/api.ts` this file is imported by *both* sides — the browser clients under the SPA
 * tsconfig and `server/src/routes/ai.ts` under the server one — and for the same reason: a route
 * and its caller must not be able to drift. It contains types and constants only, with no imports
 * and nothing DOM- or Node-specific.
 *
 * ## Why it exists
 *
 * The proxy used to restate the client's model lists rather than import them, on the reasoning that
 * "the client lives under the browser tsconfig" and that a mismatch would announce itself as a
 * refused render naming the model. Both halves of that were wrong. `shared/` compiles under both
 * tsconfigs, which is the whole point of the directory; and the mismatch that actually happened was
 * silent on the only deployment anyone develops against — the Magic Wand and the Texture Forge
 * default to `gpt-5.6-terra`, which was missing from the `site-plan` allow-list, so the wand worked
 * perfectly in direct mode and was refused outright the moment it ran behind the proxy.
 *
 * One list, imported twice, cannot do that.
 *
 * ## The default model is part of the contract
 *
 * Every operation names what it runs on when the caller names nothing, and those defaults are cost
 * decisions rather than quality ones — see `docs/PRICING_IMPLEMENTATION_PLAN.md`. Keeping them here
 * rather than beside each client means the routing table can be read in one screen, which is what
 * makes it auditable: "everything runs on the flagship" is easy to end up with by accident and hard
 * to notice one module at a time.
 *
 * A dropdown that names a model still wins. The default is what you get for not choosing, and most
 * calls are made by code rather than by a person choosing.
 */

export type AiOperation = 'enhance' | 'site-plan' | 'swatch' | 'wand' | 'forge' | 'tag-suggest';

/** Every operation, in the order the pricing table lists them. */
export const AI_OPERATIONS: readonly AiOperation[] = [
  'tag-suggest',
  'site-plan',
  'wand',
  'forge',
  'swatch',
  'enhance',
];

/**
 * Models the Responses API will serve for this app.
 *
 * A curated allow-list, not a mirror of the account's catalogue: its job is to stop an authenticated
 * caller turning `/api/ai/*` into a general-purpose OpenAI relay, so it should name what the app
 * actually offers and nothing else. The three GPT-5.6 tiers are what the dropdowns present; the
 * older entries exist because a key without 5.6 access should still be able to run the tool.
 */
export const RESPONSES_MODELS: readonly string[] = [
  'gpt-5.6-sol',
  'gpt-5.6-terra',
  'gpt-5.6-luna',
  'gpt-5.6',
  'gpt-5.5',
  'gpt-5',
  'gpt-4.1',
  'o4-mini',
];

/** The image models. `gpt-image-1` stays listed so an older key is not locked out. */
export const IMAGE_MODELS: readonly string[] = ['gpt-image-2', 'gpt-image-1'];

const RESPONSES_URL = 'https://api.openai.com/v1/responses';

export interface AiOperationSpec {
  /** The OpenAI endpoint — posted to directly in local mode, and by the proxy when hosted. */
  upstream: string;
  /** The same-origin path in proxy mode. */
  proxyPath: string;
  /** Models this operation may name. Anything else is refused before a request is made. */
  models: readonly string[];
  /** What it runs on when the caller names nothing. */
  defaultModel: string;
  /** Why that default, in one line. Read by nobody at runtime and by everybody at review time. */
  rationale: string;
  /**
   * How a call is priced, which decides what the ledger reads off it.
   *
   * `tokens` calls report `usage` in the response body; `images` calls are priced per image from
   * the size, quality and count in the *request*. Stated here rather than inferred from the model
   * or the URL, because the inference would be a guess that happens to be right today.
   */
  metering: 'tokens' | 'images';
}

export const AI_OPERATION_SPECS: Record<AiOperation, AiOperationSpec> = {
  'tag-suggest': {
    upstream: RESPONSES_URL,
    proxyPath: '/api/ai/tag-suggest',
    models: RESPONSES_MODELS,
    defaultModel: 'gpt-5.6-terra',
    rationale:
      'A small structured extraction from one photograph, offered as a free-feeling convenience. ' +
      'Worth measuring on Luna once there is a ledger to measure it with.',
    metering: 'tokens',
  },
  'site-plan': {
    upstream: RESPONSES_URL,
    proxyPath: '/api/ai/site-plan',
    models: RESPONSES_MODELS,
    defaultModel: 'gpt-5.6-sol',
    rationale:
      'Five stages of competing constraints, read once per site, with every later judgement ' +
      'inheriting the first one’s errors. The flagship is worth it here and nowhere else.',
    metering: 'tokens',
  },
  wand: {
    upstream: RESPONSES_URL,
    proxyPath: '/api/ai/wand',
    models: RESPONSES_MODELS,
    defaultModel: 'gpt-5.6-terra',
    rationale: 'Called in a loop while someone iterates, and the cost of a wrong answer is an undo.',
    metering: 'tokens',
  },
  forge: {
    upstream: RESPONSES_URL,
    proxyPath: '/api/ai/forge',
    models: RESPONSES_MODELS,
    defaultModel: 'gpt-5.6-terra',
    rationale: 'One grounded research call per material, and the grounding does the work.',
    metering: 'tokens',
  },
  swatch: {
    upstream: 'https://api.openai.com/v1/images/generations',
    proxyPath: '/api/ai/swatch',
    models: IMAGE_MODELS,
    defaultModel: 'gpt-image-2',
    rationale: 'The only image model on offer; the cost lever here is size and quality, not model.',
    metering: 'images',
  },
  enhance: {
    upstream: 'https://api.openai.com/v1/images/edits',
    proxyPath: '/api/ai/enhance',
    models: IMAGE_MODELS,
    defaultModel: 'gpt-image-2',
    rationale: 'As above. Quality tier is chosen per render and priced accordingly.',
    metering: 'images',
  },
};

/** What an operation runs on when the caller names nothing. */
export function defaultModelFor(operation: AiOperation): string {
  return AI_OPERATION_SPECS[operation].defaultModel;
}

/** Whether this operation may name this model. */
export function isModelAllowed(operation: AiOperation, model: string): boolean {
  return AI_OPERATION_SPECS[operation].models.includes(model);
}

/**
 * Every model any operation can reach, deduplicated.
 *
 * Exists for the test that walks the client default constants and asserts the proxy would accept
 * each one — the check that would have caught the wand being refused in hosted mode.
 */
export function allAllowedModels(): readonly string[] {
  const seen = new Set<string>();
  for (const operation of AI_OPERATIONS) {
    for (const model of AI_OPERATION_SPECS[operation].models) seen.add(model);
  }
  return [...seen];
}
